/**
 * Slack Service
 *
 * Sends notifications to Slack channel via Firebase Cloud Functions.
 * Used for error reporting, delayed quiz results, and support notifications.
 *
 * Uses the same Cloud Function as the mobile app (firebase/functions/api_manager.js)
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase';

// Initialize Firebase Functions
const functions = getFunctions(app);

// Reference to the private API Cloud Function (same as mobile app uses)
const ffPrivateApiCall = httpsCallable(functions, 'ffPrivateApiCall');

/**
 * Send a message to the Slack support channel
 *
 * @param {string} message - The message to send to Slack
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendSlackMessage = async (message) => {
  try {
    console.log('📤 Sending Slack notification...');

    const response = await ffPrivateApiCall({
      callName: 'SendMessageCall',
      variables: {
        message: message,
        // token is read from Firebase Functions config on the server side
      },
    });

    const result = response.data;

    if (result.statusCode === 200) {
      console.log('✅ Slack notification sent successfully');
      return { success: true };
    } else {
      console.error('❌ Slack notification failed:', result);
      return { success: false, error: result.error || 'Unknown error' };
    }
  } catch (error) {
    console.error('❌ Error sending Slack notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Notify Slack when quiz results are delayed (timeout)
 *
 * @param {string} userEmail - User's email address
 * @param {string} sessionId - Firestore session ID
 * @param {number} timeoutMs - How long we waited before timing out
 */
export const notifyQuizResultTimeout = async (userEmail, sessionId, timeoutMs) => {
  const message = `⏰ Quiz result timeout for web quiz:\n` +
    `• Email: ${userEmail || 'Not provided'}\n` +
    `• Session: ${sessionId}\n` +
    `• Timeout: ${timeoutMs / 1000}s\n` +
    `• Time: ${new Date().toISOString()}`;

  return sendSlackMessage(message);
};

/**
 * Notify Slack when quiz API submission fails
 *
 * @param {string} userName - User's display name
 * @param {string} sessionId - Firestore session ID
 * @param {string} errorDetails - Error message or response details
 */
export const notifyQuizApiError = async (userName, sessionId, errorDetails) => {
  const message = `❌ Quiz API error for web quiz:\n` +
    `• User: ${userName || 'Not provided'}\n` +
    `• Session: ${sessionId}\n` +
    `• Error: ${errorDetails}\n` +
    `• Time: ${new Date().toISOString()}`;

  return sendSlackMessage(message);
};

/**
 * Notify Slack when profile creation fails
 *
 * @param {string} userName - User's display name
 * @param {string} userId - Firebase user ID
 * @param {string} sessionId - Firestore session ID
 * @param {string} errorDetails - Error message or response details
 */
export const notifyProfileCreationError = async (userName, userId, sessionId, errorDetails) => {
  const message = `❌ Profile creation failed for web quiz:\n` +
    `• User: ${userName || 'Not provided'}\n` +
    `• User ID: ${userId || 'Not provided'}\n` +
    `• Session: ${sessionId || 'Not provided'}\n` +
    `• Error: ${errorDetails}\n` +
    `• Time: ${new Date().toISOString()}`;

  return sendSlackMessage(message);
};

/**
 * Notify Slack for general errors/issues
 *
 * @param {string} context - Where the error occurred
 * @param {string} userNameOrEmail - User's name or email (if available)
 * @param {string} sessionId - Firestore session ID (if available)
 * @param {string} errorDetails - Error details
 */
export const notifyGeneralError = async (context, userNameOrEmail, sessionId, errorDetails) => {
  const message = `🚨 Web Quiz Error:\n` +
    `• Context: ${context}\n` +
    `• User: ${userNameOrEmail || 'Not provided'}\n` +
    `• Session: ${sessionId || 'Not provided'}\n` +
    `• Error: ${errorDetails}\n` +
    `• Time: ${new Date().toISOString()}`;

  return sendSlackMessage(message);
};

export default {
  sendSlackMessage,
  notifyQuizResultTimeout,
  notifyQuizApiError,
  notifyProfileCreationError,
  notifyGeneralError,
};
